import { GridCellProps } from '@progress/kendo-react-grid';
import { splitNumberLookup } from 'admin/pages/ReportsPage/static';
import { AdminCheckBox } from 'app/components/AdminCheckbox';
import AdminCustomButton from 'app/components/AdminCustomButton';
import { AdminDatePicker } from 'app/components/AdminDatePicker';
import AdminDropDown from 'app/components/AdminDropDown';
import { DeleteIcon } from 'app/assets/icons';
import { SetStateAction, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GeneratedSplit, RiderSplitClass } from 'services/openapi';
import styled from 'styled-components';
import { isoString } from 'utils/common';

type Props = {
  e: GridCellProps;
  onViewClick?: (e) => void;
  onClassACheckboxChange?: (e: any, event: any, className: any) => void;
  isDisabled?: true;
  onSaveClick?: (requestBody: Array<RiderSplitClass>) => void;
  onClassUncheck?: (item: RiderSplitClass) => void;
  onClassDelete?: (showRiderIds: string[]) => void;
  setSplitData?: React.Dispatch<
    SetStateAction<Array<GeneratedSplit> | undefined>
  >;
  setTransformedData?: React.Dispatch<SetStateAction<Array<any>>>;
  itemIndex?: number | undefined;
  transformedData?: any[];
  isSplit?: boolean;
};

const ManageRiderClassSplitUpdateCell = ({
  e,
  onViewClick,
  onClassACheckboxChange,
  isDisabled,
  onSaveClick,
  onClassUncheck,
  onClassDelete,
  setSplitData,
  setTransformedData,
  itemIndex,
  transformedData,
  isSplit = false,
}: Props) => {
  const data = isSplit
    ? e.dataItem.classes?.filter(item => item !== null)
    : e.dataItem.class?.filter(item => item !== null);
  const dataindex = e.dataItem.index;
  let prePayload = data?.map(item => {
    return {
      showRiderId: item?.showRiderId,
      riderClass: item?.riderClass,
      split: item?.split,
      scratch: item?.scratch ?? false,
      scratchDate: item?.scratchDate || null,
    };
  });
  const [payload, setPayload] = useState<Array<RiderSplitClass>>(prePayload);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [splitEnabled, setSplitEnabled] = useState<boolean[]>(() =>
    (prePayload ?? []).map(item => !!(item.split?.id && item.split.id !== '')),
  );
  const [markedForDelete, setMarkedForDelete] = useState<Set<number>>(
    new Set(),
  );
  const currentLocation = useLocation();
  const isPastDataFlag = currentLocation.pathname.includes('past-data');
  const [validationErrors, setValidationErrors] = useState<{
    [key: number]: string;
  }>({});

  // Helper function to check if save button should be enabled
  const shouldEnableSaveButton = (
    currentPayload: Array<RiderSplitClass>,
    currentMarkedForDelete: Set<number>,
  ) => {
    return (
      currentMarkedForDelete.size > 0 ||
      currentPayload?.some(
        item =>
          item.scratch === true ||
          (item.split && item.split.id && item.split.id !== ''),
      ) ||
      false
    );
  };
  const validateScratchDate = (payloadToValidate: Array<RiderSplitClass>) => {
    const errors: { [key: number]: string } = {};

    payloadToValidate?.forEach((item, index) => {
      if (item.scratch && !item.scratchDate && !markedForDelete.has(index)) {
        errors[index] = 'Scratch date is required';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = () => {
    const remainingPayload = (payload ?? []).filter(
      (_, i) => !markedForDelete.has(i),
    );
    if (validateScratchDate(remainingPayload)) {
      if (markedForDelete.size > 0) {
        const showRiderIds = (payload ?? [])
          .filter((_, i) => markedForDelete.has(i))
          .map(item => item.showRiderId ?? '')
          .filter(id => id !== '');
        onClassDelete?.(showRiderIds);
        setMarkedForDelete(new Set());
      }
      const hasSplitChanges = remainingPayload.some(
        item =>
          item.scratch === true ||
          (item.split && item.split.id && item.split.id !== ''),
      );
      if (hasSplitChanges) {
        onSaveClick?.(remainingPayload);
      }
      setValidationErrors({});
    }
  };
  const onDropdownChange = (e, index, itemIndex) => {
    let newPayload: RiderSplitClass[] = (payload ?? [])
      ?.filter(
        (item): item is RiderSplitClass =>
          item !== undefined &&
          item.showRiderId !== undefined &&
          item.riderClass !== undefined &&
          item.split !== undefined &&
          item.scratch !== undefined,
      )
      ?.map((item, i) => {
        if (index === i) {
          return {
            showRiderId: item.showRiderId ?? '',
            riderClass: item.riderClass,
            split: { id: e.id, name: e.name },
            scratch: item.scratch,
            scratchDate: item.scratchDate,
          };
        }
        return item; // return unchanged item if index does not match
      });
    if (newPayload) {
      let filteredPayload: RiderSplitClass[] = newPayload.filter(
        (item): item is RiderSplitClass => item !== undefined,
      );
      setPayload(filteredPayload);
      setIsSaveDisabled(
        !shouldEnableSaveButton(filteredPayload, markedForDelete),
      );
      if (validationErrors[index]) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
      }
    }

    if (!transformedData) return;
    else {
      if (isSplit) {
        const mappedData = transformedData?.map((item, tindex) => {
          if (tindex === itemIndex) {
            return {
              ...item,
              riders: item.riders?.map((rider, rideIndex) => {
                if (rideIndex === dataindex) {
                  return {
                    ...rider,
                    classes: rider.classes?.map((cls, clsIndex) => {
                      if (clsIndex === index) {
                        return { ...cls, split: { id: e.id, name: e.name } };
                      }
                      return cls;
                    }),
                  };
                }
                return rider;
              }),
            };
          }
          return item;
        });
        setTransformedData?.(mappedData);
      } else {
        const mappedData = transformedData?.map((item, tindex) => {
          if (tindex === itemIndex) {
            return {
              ...item,
              riders: item.riders?.map((rider, rideIndex) => {
                if (rideIndex === dataindex) {
                  return {
                    ...rider,
                    class: rider.class?.map((cls, clsIndex) => {
                      if (clsIndex === index) {
                        return { ...cls, split: { id: e.id, name: e.name } };
                      }
                      return cls;
                    }),
                  };
                }
                return rider;
              }),
            };
          }
          return item;
        });
        setTransformedData?.(mappedData);
      }
    }
  };

  const onSplitCheckboxChange = (e, index) => {
    let newPayload: RiderSplitClass[] = (payload ?? [])
      ?.filter(
        (item): item is RiderSplitClass =>
          item !== undefined &&
          item.showRiderId !== undefined &&
          item.riderClass !== undefined &&
          item.split !== undefined &&
          item.scratch !== undefined,
      )
      ?.map((item, i) => {
        if (index === i) {
          return {
            showRiderId: item.showRiderId ?? '',
            riderClass: item.riderClass,
            split: item.split,
            scratch: e.value,
            scratchDate: e.value ? item.scratchDate : null, // Reset scratchDate if unchecked
          };
        }
        return item;
      });
    if (newPayload) {
      let filteredPayload: RiderSplitClass[] = newPayload?.filter(
        (item): item is RiderSplitClass => item !== undefined,
      );
      setPayload(filteredPayload);
      // Update save button state based on whether any scratch checkbox is checked
      setIsSaveDisabled(
        !shouldEnableSaveButton(filteredPayload, markedForDelete),
      );
      if (!e.value && validationErrors[index]) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
      }
    }
  };
  const onScratchDateChange = (e, index) => {
    const date = e.value;
    let newPayload: RiderSplitClass[] = (payload ?? [])
      ?.filter(
        (item): item is RiderSplitClass =>
          item !== undefined &&
          item.showRiderId !== undefined &&
          item.riderClass !== undefined &&
          item.split !== undefined &&
          item.scratch !== undefined,
      )
      ?.map((item, i) => {
        if (index === i) {
          return {
            showRiderId: item.showRiderId ?? '',
            riderClass: item.riderClass,
            split: item.split,
            scratch: item.scratch,
            scratchDate: date ? isoString(date) : null,
          };
        }
        return item;
      });
    if (newPayload) {
      let filteredPayload: RiderSplitClass[] = newPayload?.filter(
        (item): item is RiderSplitClass => item !== undefined,
      );
      setPayload(filteredPayload);
      setIsSaveDisabled(
        !shouldEnableSaveButton(filteredPayload, markedForDelete),
      );
      // Clear validation error when date is selected
      if (date && validationErrors[index]) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
      }
    }

    if (!transformedData) return;
    else {
      if (isSplit) {
        const mappedData = transformedData?.map((item, tindex) => {
          if (tindex === itemIndex) {
            return {
              ...item,
              riders: item.riders?.map((rider, rideIndex) => {
                if (rideIndex === dataindex) {
                  return {
                    ...rider,
                    classes: rider.classes?.map((cls, clsIndex) => {
                      if (clsIndex === index) {
                        return {
                          ...cls,
                          scratchDate: date ? isoString(date) : null,
                        };
                      }
                      return cls;
                    }),
                  };
                }
                return rider;
              }),
            };
          }
          return item;
        });

        setTransformedData?.(mappedData);
      } else {
        const mappedData = transformedData?.map((item, tindex) => {
          if (tindex === itemIndex) {
            return {
              ...item,
              riders: item.riders?.map((rider, rideIndex) => {
                if (rideIndex === dataindex) {
                  return {
                    ...rider,
                    class: rider.class?.map((cls, clsIndex) => {
                      if (clsIndex === index) {
                        return {
                          ...cls,
                          scratchDate: date ? isoString(date) : null,
                        };
                      }
                      return cls;
                    }),
                  };
                }
                return rider;
              }),
            };
          }
          return item;
        });

        setTransformedData?.(mappedData);
      }
    }
  };

  useEffect(() => {
    setPayload(prePayload);
    const initialSplitEnabled = (prePayload ?? []).map(
      item => !!(item.split?.id && item.split.id !== ''),
    );
    setSplitEnabled(initialSplitEnabled);
    setMarkedForDelete(new Set());
    setIsSaveDisabled(!shouldEnableSaveButton(prePayload ?? [], new Set()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledTd
      style={e.style}
      className={e.className}
      onClick={() => onViewClick?.(data)}
    >
      <CustomTableColumnWrapper>
        <div>
          {data?.map((item, index) => {
            const usePrePayload = payload && payload.length > 0;
            const defaultValue = usePrePayload ? payload[index] : item;
            const hasValidationError = validationErrors[index];
            return (
              <SplitWrapper isMarked={markedForDelete.has(index)}>
                <div>
                  <CheckboxSplitWrapper>
                    <AdminCheckBox
                      onChange={event => {
                        if (isPastDataFlag) return;
                        const checked = event?.value;
                        const newSplitEnabled = [...splitEnabled];
                        newSplitEnabled[index] = !!checked;
                        setSplitEnabled(newSplitEnabled);
                        if (!checked) {
                          const updated = (payload ?? []).map((p, i) =>
                            i === index
                              ? { ...p, split: { id: '', name: '' } }
                              : p,
                          );
                          setPayload(updated);
                          setIsSaveDisabled(
                            !shouldEnableSaveButton(updated, markedForDelete),
                          );
                          onClassUncheck?.(updated[index]);
                        }
                      }}
                      value={splitEnabled[index] ?? false}
                      label={`${item?.group + ' ' + item?.riderClass?.name}`}
                      title={`${item?.group + ' ' + item?.riderClass?.name}`}
                      className="checkbox-style"
                    />
                    <AdminDropDown
                      label=""
                      onDataChange={event => {
                        if (event) {
                          onDropdownChange(event, index, itemIndex);
                        }
                      }}
                      defaultValue={defaultValue.split}
                      placeholder="Choose Split Number"
                      data={splitNumberLookup}
                      className="field-style"
                      disabled={
                        !splitEnabled[index] ||
                        isPastDataFlag ||
                        markedForDelete.has(index)
                      }
                    />
                    {!isDisabled && !isPastDataFlag && (
                      <BinButton
                        onClick={() => {
                          setMarkedForDelete(prev => {
                            const next = new Set(prev);
                            if (next.has(index)) next.delete(index);
                            else next.add(index);
                            setIsSaveDisabled(
                              !shouldEnableSaveButton(payload ?? [], next),
                            );
                            return next;
                          });
                        }}
                        isMarked={markedForDelete.has(index)}
                        title={
                          markedForDelete.has(index)
                            ? 'Undo remove'
                            : 'Remove from class'
                        }
                      >
                        <DeleteIcon />
                      </BinButton>
                    )}
                  </CheckboxSplitWrapper>
                  {!isDisabled && (
                    <CheckboxSplitWrapper>
                      <AdminCheckBox
                        onChange={event => {
                          if (event) {
                            if (isPastDataFlag) {
                              return;
                            } else {
                              onSplitCheckboxChange(event, index);
                            }
                          }
                        }}
                        value={defaultValue.scratch}
                        label="Scratch"
                        className="checkbox-style"
                      />
                      <DatepickerWrapper>
                        <AdminDatePicker
                          placeholder="Scratch Date"
                          value={
                            defaultValue.scratchDate
                              ? new Date(defaultValue.scratchDate)
                              : undefined
                          }
                          onChange={e => onScratchDateChange(e, index)}
                          className="field-style create-date"
                          disabled={!defaultValue.scratch || isPastDataFlag}
                          inputDisable
                        />
                        {hasValidationError && (
                          <Errortext>{hasValidationError}</Errortext>
                        )}
                      </DatepickerWrapper>
                    </CheckboxSplitWrapper>
                  )}
                </div>
              </SplitWrapper>
            );
          })}
        </div>
        {!isPastDataFlag && !isDisabled && (
          <ButtonWrapper>
            <AdminCustomButton
              buttonType="save"
              onClick={handleSaveClick}
              disabled={isSaveDisabled}
            >
              Save
            </AdminCustomButton>
          </ButtonWrapper>
        )}
      </CustomTableColumnWrapper>
    </StyledTd>
  );
};

export default ManageRiderClassSplitUpdateCell;
const BinButton = styled.button<{ isMarked: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  svg {
    opacity: ${p => (p.isMarked ? 1 : 0.4)};
    color: ${p => (p.isMarked ? '#f44336' : 'inherit')};
    path {
      fill: ${p => (p.isMarked ? '#f44336' : '')};
    }
  }
  &:hover svg {
    opacity: 1;
  }
`;
const DatepickerWrapper = styled.div``;
const Errortext = styled.span`
  display: block;
  color: #f44336;
  text-align: left;
  font-family: 'inter';
  &.checkbox-error-text {
    padding-left: 20px;
  }
`;
const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;
const StyledTd = styled.td`
  .checkbox-style label {
    display: block !important;
  }
`;
const CustomTableColumnWrapper = styled.div`
  display: flex;
  gap: 20px;
  .SHdropdown {
    width: 60%;
    .k-dropdownlist {
      max-height: 31px;
      width: 200px;
      border-radius: 8px !important;
      font-size: 13px;
      .k-input-value-text {
        font-size: 13px;
        font-family: 'Inter';
        cursor: pointer;
        font-weight: 500;
        line-height: 19.6px;
      }
    }
  }
`;
const SplitWrapper = styled.div<{ isMarked?: boolean }>`
  display: block;
  gap: 20px;
  opacity: ${p => (p.isMarked ? 0.5 : 1)};
  text-decoration: ${p => (p.isMarked ? 'line-through' : 'none')};
  .checkbox-style {
    label {
      width: 175px;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }
`;
const CheckboxSplitWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  .custom-calendar-wrapper {
    min-height: auto;
    .k-input-solid {
      height: 31px;
      max-width: 200px;
    }
  }
`;
